import { useState } from 'react';
import { message } from 'antd';

/**
 * Manages form submission, delete, and confirm-only flows for DynamicPopup.
 *
 * @param {Object} params
 * @param {Object} params.form - Ant Design form instance
 * @param {Object|null} params.config - Popup config
 * @param {Object} params.api - { submit?, deleteItems?, onOk? }
 * @param {Object} params.popupContext
 * @param {string[]} params.resolvedIds
 * @param {Object} params.dynamicOptions
 * @param {*} params.extraData
 * @param {string[]} params.removedIds
 * @param {Array} params.fields
 * @param {Function} params.onClose
 * @param {Function} [params.onSubmit] - Fallback submit handler
 * @returns {{ handleOk: Function, confirming: boolean, deleteError: string|null, resetSubmitState: Function }}
 */
export function usePopupSubmit({
  form,
  config,
  api,
  popupContext,
  resolvedIds,
  dynamicOptions,
  extraData,
  removedIds,
  fields,
  onClose,
  onSubmit,
}) {
  const [confirming, setConfirming] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  async function runSubmit(values) {
    const submitPayload = { resolvedIds, dynamicOptions, extraData, removedIds };
    if (typeof api.submit === 'function') {
      return api.submit(values, popupContext, submitPayload);
    }
    if (typeof onSubmit === 'function') {
      return onSubmit(values, popupContext, submitPayload);
    }
  }

  function handleOk() {
    if (!config) return;

    // No fields → delete mode, onOk shortcut, or plain close
    if (!fields.length) {
      if (config.mode === 'delete' && typeof api.deleteItems === 'function') {
        setConfirming(true);
        setDeleteError(null);
        Promise.resolve(api.deleteItems(resolvedIds, popupContext))
          .then(() => onClose())
          .catch((err) => {
            const msg = err?.message || 'Delete failed. Please try again.';
            setDeleteError(msg);
            setConfirming(false);
            message.error(msg);
          });
        return;
      }

      if (typeof api.onOk === 'function') {
        setConfirming(true);
        Promise.resolve(api.onOk(popupContext))
          .then(() => onClose())
          .catch((err) => {
            const msg = err?.message || 'Failed. Please try again.';
            message.error(msg);
            setConfirming(false);
          });
        return;
      }

      onClose();
      return;
    }

    // Fields present → validate then submit
    form
      .validateFields()
      .then(async (values) => {
        setConfirming(true);
        try {
          const result = await runSubmit(values);
          if (result?.navigated) return;
          message.success(config.successMessage || 'Saved successfully');
          onClose();
        } catch (err) {
          message.error(err?.message || 'Failed. Please try again.');
        } finally {
          setConfirming(false);
        }
      })
      .catch(() => {
        // validateFields rejects when validation fails — Ant Design displays inline field errors
      });
  }

  /** Resets submit state; call this when the popup opens to clear any previous error. */
  function resetSubmitState() {
    setConfirming(false);
    setDeleteError(null);
  }

  return { handleOk, confirming, deleteError, resetSubmitState };
}
